/**
 * Latency analysis — static interval-arithmetic estimation over the ProcessModel IR.
 * Design of record: docs/superpowers/plans/2026-07-07-latency-analysis.md.
 *
 * The fold prices each leaf as a `(system, business)` pair of {@link Bound}s whose
 * upper is either finite or OPEN (with reasons split *structural* vs *gap*), and a
 * `priced` upper tracked over priced content only. Composition is interval
 * arithmetic (sum / max / min / order-statistic). An interface's `latency`
 * requirement caps its modeled SYSTEM internals (business passes through); a
 * declared entry (leading inbound) promise is the envelope the rest of the flow
 * nests in. Estimation gaps are analysis data, never diagnostics — only malformed
 * values warn (surfaced by the caller via {@link LatencyEstimate.malformed}).
 *
 * Pure: all model lookups (interface latency, actor detection, subprocess bodies)
 * are injected via {@link LatencyEnv}, so the fold is unit-testable without the
 * resolver. `buildLatencyEnv` (process-latency-env.ts) wires it to a ResolvedModel.
 */

import { parseDuration, formatInterval, formatDuration } from "@archlang/calc";
import type { QualifiedName } from "./types.js";
import type {
  Flow,
  ProcessModel,
  ProcessNode,
  SubprocessModel,
  CallNode,
  AwaitNode,
} from "./process-model.js";
import type { WalkResult, WalkStep } from "./process-walker-types.js";
import { isAwaitNodeId } from "./process-walker-types.js";
import { forEachNode } from "./process-ir-walk.js";

// =============================================================================
// Reasons — why an upper bound is OPEN
// =============================================================================

export type StructuralReason = "go" | "recursive-splice" | "condition-wait" | "inbound-wait";
export type GapReason = "missing-latency" | "malformed-latency" | "unbounded-each" | "cron-wait";
export type Reason = StructuralReason | GapReason;

const STRUCTURAL: ReadonlySet<Reason> = new Set<Reason>([
  "go",
  "recursive-splice",
  "condition-wait",
  "inbound-wait",
]);

export function isStructural(reasons: Iterable<Reason>): boolean {
  for (const r of reasons) if (STRUCTURAL.has(r)) return true;
  return false;
}

// =============================================================================
// Bound + Timed
// =============================================================================

/**
 * One bucket's interval. `hi` is `Infinity` when {@link open}; `priced` is the
 * upper computed over priced content only (finite, and === `hi` when closed —
 * the closed-bound invariant).
 */
export interface Bound {
  readonly lo: number;
  readonly hi: number; // Infinity when open
  readonly open: boolean;
  readonly reasons: ReadonlySet<Reason>;
  readonly priced: number;
  /** Count of unpriced (gap) leaves contributing here. Additive under every
   *  combinator; zeroed when a promise absorbs a system subtree. */
  readonly unest: number;
}

/** The `(system, business)` decomposition plus the combined `total`. `total` is
 *  the load-bearing figure; the buckets are the decomposition of the **winning
 *  path** (the branch that determines the total's upper) — so a merge bounded by
 *  a business fallback correctly attributes the bound to `business`. They may not
 *  SUM to total across a selection (the winning path's lo/hi need not tile the
 *  combined interval), but each corresponds to a real path, never a per-bucket
 *  mixture. */
export interface Timed {
  readonly total: Bound;
  readonly system: Bound;
  readonly business: Bound;
}

const ZERO_BOUND: Bound = { lo: 0, hi: 0, open: false, reasons: new Set(), priced: 0, unest: 0 };
export const ZERO: Timed = { total: ZERO_BOUND, system: ZERO_BOUND, business: ZERO_BOUND };

function closed(lo: number, hi: number): Bound {
  return { lo, hi, open: false, reasons: new Set(), priced: hi, unest: 0 };
}

/** An open bound: known lower bound `lo`, priced upper `priced`, open for `reasons`.
 *  `unest` counts this as a data gap (1) vs a structural open (0). */
function open(lo: number, priced: number, reasons: Iterable<Reason>, unest = 0): Bound {
  return { lo, hi: Infinity, open: true, reasons: new Set(reasons), priced, unest };
}

function timedSystem(b: Bound): Timed {
  return { total: b, system: b, business: ZERO_BOUND };
}
function timedBusiness(b: Bound): Timed {
  return { total: b, system: ZERO_BOUND, business: b };
}

function unionReasons(bs: readonly Bound[], onlyOpen: boolean): Set<Reason> {
  const out = new Set<Reason>();
  for (const b of bs) {
    if (onlyOpen && !b.open) continue;
    for (const r of b.reasons) out.add(r);
  }
  return out;
}

// =============================================================================
// Bound combinators
// =============================================================================

function sumUnest(bs: readonly Bound[]): number {
  return bs.reduce((n, b) => n + b.unest, 0);
}

function sumB(bs: readonly Bound[]): Bound {
  if (bs.length === 0) return ZERO_BOUND;
  let lo = 0;
  let hi = 0;
  let priced = 0;
  let anyOpen = false;
  for (const b of bs) {
    lo += b.lo;
    hi += b.hi;
    priced += b.priced;
    if (b.open) anyOpen = true;
  }
  const unest = sumUnest(bs);
  if (!anyOpen) return { lo, hi, open: false, reasons: new Set(), priced, unest };
  return { lo, hi: Infinity, open: true, reasons: unionReasons(bs, true), priced, unest };
}

/** Join-all / branch-max: wait for every branch. */
function maxB(bs: readonly Bound[]): Bound {
  if (bs.length === 0) return ZERO_BOUND;
  const lo = Math.max(...bs.map((b) => b.lo));
  const priced = Math.max(...bs.map((b) => b.priced));
  const unest = sumUnest(bs);
  const anyOpen = bs.some((b) => b.open);
  if (!anyOpen) {
    const hi = Math.max(...bs.map((b) => b.hi));
    return { lo, hi, open: false, reasons: new Set(), priced, unest };
  }
  return { lo, hi: Infinity, open: true, reasons: unionReasons(bs, true), priced, unest };
}

/** Branch alternation (if/select case): the result may be any arm. lo = min arm lo. */
function altB(bs: readonly Bound[]): Bound {
  if (bs.length === 0) return ZERO_BOUND;
  const lo = Math.min(...bs.map((b) => b.lo));
  const priced = Math.max(...bs.map((b) => b.priced));
  const unest = sumUnest(bs);
  const anyOpen = bs.some((b) => b.open);
  if (!anyOpen) {
    const hi = Math.max(...bs.map((b) => b.hi));
    return { lo, hi, open: false, reasons: new Set(), priced, unest };
  }
  return { lo, hi: Infinity, open: true, reasons: unionReasons(bs, true), priced, unest };
}

/** N-th order statistic (join N / race N). Closed iff ≥ n finite uppers. */
function orderStatB(bs: readonly Bound[], n: number): Bound {
  if (bs.length === 0) return ZERO_BOUND;
  const k = Math.max(1, Math.min(n, bs.length));
  const hisSorted = [...bs].map((b) => (b.open ? Infinity : b.hi)).sort((a, b) => a - b);
  const losSorted = [...bs].map((b) => b.lo).sort((a, b) => a - b);
  const pricedSorted = [...bs].map((b) => b.priced).sort((a, b) => a - b);
  const finiteCount = bs.filter((b) => !b.open).length;
  const lo = losSorted[k - 1]!;
  const unest = sumUnest(bs);
  if (finiteCount >= k) {
    const hi = hisSorted[k - 1]!; // the k-th smallest upper is finite
    return { lo, hi, open: false, reasons: new Set(), priced: hi, unest };
  }
  // stays open: fewer than k finite uppers
  return { lo, hi: Infinity, open: true, reasons: unionReasons(bs, true), priced: pricedSorted[k - 1]!, unest };
}

/** Race / min (cheapest-satisfying): an open branch is +∞, so the min closes
 *  whenever any branch is finite. */
function minB(bs: readonly Bound[]): Bound {
  return orderStatB(bs, 1);
}

/** SUM composes componentwise — the buckets genuinely add and sum to the total. */
function sumTimed(ts: readonly Timed[]): Timed {
  return componentwise(ts, sumB);
}

/** A branch's total upper as a rank (OPEN = +∞), for selecting the hi-winner. */
function rankHi(t: Timed): number {
  return t.total.open ? Infinity : t.total.hi;
}
const rankLo = (t: Timed): number => t.total.lo;

/**
 * One dimension's bound for a selection: its **lower** comes from the best-case
 * (lo-winning) branch and its **upper/open** from the worst-case (hi-winning)
 * branch — mirroring how the combinator derives `total.lo`/`total.hi` from
 * (possibly different) branches. So a race you MIGHT skip contributes lo 0 (you
 * needn't incur it), while the branch that BOUNDS you contributes the upper —
 * e.g. `(checks) or override` → business `[0, 1d]` (0 when the fast checks win;
 * ≤1d via the human backstop), never a misleading `[1h, 1d]`.
 */
function pickBucket(loW: Bound, hiW: Bound, pricedOverride?: number): Bound {
  if (hiW.open) {
    // Clamp `lo` to the RESULT's priced (the override when given), not the
    // hi-winner's — else a race whose lo-winner can be skipped in favour of a
    // cheaper-priced branch fabricates lo > priced (a guaranteed wait the run
    // needn't incur). `priced` is the open bound's upper, so lo ≤ priced always.
    const priced = pricedOverride ?? hiW.priced;
    const lo = Math.min(loW.lo, priced);
    return { lo, hi: Infinity, open: true, reasons: new Set(hiW.reasons), priced, unest: hiW.unest };
  }
  const lo = Math.min(loW.lo, hiW.hi);
  return { lo, hi: hiW.hi, open: false, reasons: new Set(), priced: hiW.hi, unest: hiW.unest };
}

/**
 * A SELECTION combinator (max / min / order-statistic / branch-alternation):
 * the total composes via the Bound combinator `f`; each `(system, business)`
 * dimension is rebuilt from the branch achieving the total's lower and the
 * branch achieving its upper (see {@link pickBucket}) — never a per-bucket
 * independent combine (which lands on a branch matching no real path), and never
 * the hi-winner's whole interval (whose `lo` would fabricate a guaranteed wait).
 */
function selectTimed(
  ts: readonly Timed[],
  f: (bs: readonly Bound[]) => Bound,
  loIdx: (ts: readonly Timed[]) => number,
  hiIdx: (ts: readonly Timed[]) => number,
): Timed {
  if (ts.length === 0) return ZERO;
  const total = f(ts.map((t) => t.total));
  const lo = ts[loIdx(ts)] ?? ts[0]!;
  const hi = ts[hiIdx(ts)] ?? ts[0]!;
  // For all-open selections (e.g. k-th order-statistic race where all branches are open),
  // compute the k-th-smallest priced per dimension so pickBucket uses that rather than the
  // hi-winner's priced (which is the worst-case, not the k-th-smallest).
  const sysTotal = f(ts.map((t) => t.system));
  const bizTotal = f(ts.map((t) => t.business));
  return {
    total,
    system: pickBucket(lo.system, hi.system, sysTotal.priced),
    business: pickBucket(lo.business, hi.business, bizTotal.priced),
  };
}

/** Index of the branch at the n-th ascending position by `key`. */
function nthAsc(ts: readonly Timed[], n: number, key: (t: Timed) => number): number {
  const order = ts.map((_, i) => i).sort((a, b) => key(ts[a]!) - key(ts[b]!));
  return order[Math.max(0, Math.min(n, order.length) - 1)] ?? 0;
}

/** Apply `f` to total, system, business **independently**. Correct when every
 *  dimension's worst-case is that dimension's own combine across branches:
 *  - JOIN-ALL / and-of-set (maxB): you incur all branches concurrently, so each
 *    dimension's worst is the slowest branch IN THAT DIMENSION (a compute-heavy
 *    branch and a wait-heavy branch each count in their own axis).
 *  - ALTERNATION (altB, if/else/select): you take one arm, but worst-case over
 *    arms is the max arm per dimension (lo = the fastest arm).
 *  A winning-path pick would drop the branch that is heavy in a NON-total-winning
 *  dimension (fleet F-1: an if/else's 5s compute arm vanishing behind a 1d wait
 *  arm) and be branch-order-dependent (F-2). */
function componentwise(ts: readonly Timed[], f: (bs: readonly Bound[]) => Bound): Timed {
  return {
    total: f(ts.map((t) => t.total)),
    system: f(ts.map((t) => t.system)),
    business: f(ts.map((t) => t.business)),
  };
}
/** Join-all / and-of-set: wait for the slowest — per dimension. */
function maxTimed(ts: readonly Timed[]): Timed {
  return componentwise(ts, maxB);
}
/** Branch alternation (if / select-one): worst arm per dimension, lo = fastest. */
function altTimed(ts: readonly Timed[]): Timed {
  return componentwise(ts, altB);
}
/**
 * Race / or-of-sets / join N: proceed at the n-th-fastest. Unlike join/alt, a
 * race is BOUNDED by a single branch — so the decomposition follows that branch
 * (winning-path), NOT a per-dimension combine (which would understate the
 * bounding branch's waits — the `(checks) or override` → business 0 bug). lo
 * from the fastest branch, hi/open from the bounding one (see {@link selectTimed}).
 */
function orderStatTimed(ts: readonly Timed[], n: number): Timed {
  return selectTimed(ts, (bs) => orderStatB(bs, n), (t) => nthAsc(t, n, rankLo), (t) => nthAsc(t, n, rankHi));
}
function minTimed(ts: readonly Timed[]): Timed {
  return orderStatTimed(ts, 1);
}

// =============================================================================
// Promise nesting (system-cap, business passthrough)
// =============================================================================

export interface NestResult {
  readonly timed: Timed;
  readonly contradiction: boolean;
}

/**
 * Nest modeled internals under a declared call promise `[plo, phi]`:
 *  - the promise stands in for the internals' SYSTEM component (both ends),
 *    absorbing gap-open system uppers;
 *  - BUSINESS passes through untouched;
 *  - known-worse (`system.lo > phi`) or structurally-open system internals are a
 *    NESTING_CONTRADICTION — the widened (worse) system is kept.
 */
export function nestUnderPromise(internals: Timed, plo: number, phi: number): NestResult {
  const sys = internals.system;
  const sysStructural = sys.open && isStructural(sys.reasons);
  const knownWorse = sys.lo > phi;
  if (sysStructural || knownWorse) {
    const widenedSys: Bound = { ...sys, lo: Math.max(sys.lo, plo) };
    return {
      timed: recombine(widenedSys, internals.business),
      contradiction: true,
    };
  }
  return { timed: recombine(closed(plo, phi), internals.business), contradiction: false };
}

/** Rebuild a Timed's `total` from its system + business components (sum). */
function recombine(system: Bound, business: Bound): Timed {
  return { total: sumB([system, business]), system, business };
}

// =============================================================================
// Environment (injected model lookups)
// =============================================================================

export type LatencyLookup =
  | { readonly kind: "value"; readonly lo: number; readonly hi: number }
  | { readonly kind: "missing" }
  | { readonly kind: "malformed"; readonly raw: string };

export interface LatencyEnv {
  /** Latency requirement of an interface leaf, by full structural path. */
  latencyOf(interfacePath: QualifiedName): LatencyLookup;
  readonly subprocessByName: ReadonlyMap<string, SubprocessModel>;
  readonly processByName?: ReadonlyMap<string, ProcessModel>;
}

// =============================================================================
// The fold
// =============================================================================

export interface LatencyEstimate {
  readonly timed: Timed;
  /** True iff the total upper is OPEN for a structural reason (genuine
   *  unboundedness). Drives `@estimatedLatency.bounded`. */
  readonly structurallyOpen: boolean;
  /** Count of gap-open leaves (missing/malformed latency, uncounted loops, cron). */
  readonly unestimated: number;
  /** Interface paths whose `latency` value was present but unparseable. */
  readonly malformed: readonly string[];
  /** True when a modeled internal contradicted a declared promise. */
  readonly nestingContradiction: boolean;
}

// =============================================================================
// Getter values + display (the P2 policy / P3 UI seam)
// =============================================================================

/** The scalar values of `@estimatedLatency[.…]` for a policy/expression, in
 *  seconds. System (compute/response) and business (waits) are **separate
 *  dimensions** — never summed into a total. The bare `@estimatedLatency` is the
 *  **system** worst-case (the response-time SLA — the natural budget target);
 *  `.business` is the human/wait worst-case, budgeted separately. A value is
 *  ∞ when structurally open, the priced worst under a data gap, else the finite
 *  upper. */
export interface LatencyGetters {
  readonly estimatedLatency: number; // = system worst-case (∞ when structurally open)
  readonly min: number; // system best-case
  readonly system: number;
  readonly business: number;
  readonly bounded: boolean;
  readonly unestimated: number;
}

function boundValue(b: Bound): number {
  if (!b.open) return b.hi;
  return isStructural(b.reasons) ? Infinity : b.priced;
}

export function latencyGetters(e: LatencyEstimate): LatencyGetters {
  return {
    estimatedLatency: boundValue(e.timed.system), // system dimension — NOT a system+business total
    min: e.timed.system.lo,
    system: boundValue(e.timed.system),
    business: boundValue(e.timed.business),
    bounded: !e.structurallyOpen,
    unestimated: e.unestimated,
  };
}

/** Resolve a subject's latency getters by its qualified key; undefined for a
 *  non-process subject (or an anonymous process). Injected on {@link
 *  import("./selector-eval.js").EvalContext} so `@estimatedLatency[.…]` evaluates
 *  in `where`/policy expressions. */
export type ProcessLatencyResolver = (subjectKey: string) => LatencyGetters | undefined;

/** The scalar value of `@estimatedLatency` + its dotted sub-getters, given the
 *  subject's resolved getters (or undefined for a non-process subject). The
 *  neutral rules match the roster's design (`@dist` precedent): a non-process
 *  subject is `undefined` for value getters and **true** for `.bounded`
 *  (vacuously bounded — a subject-less `not .bounded` must not flag the world). */
export function latencyBuiltinValue(
  sub: string | undefined,
  g: LatencyGetters | undefined
): number | boolean | undefined {
  // ∞ is a real value here (a structurally-open flow) — passed through so a
  // `> budget` comparison fires.
  if (sub === "bounded") return g ? g.bounded : true;
  if (!g) return undefined;
  switch (sub) {
    case undefined:
      return g.estimatedLatency;
    case "min":
      return g.min;
    case "system":
      return g.system;
    case "business":
      return g.business;
    case "unestimated":
      return g.unestimated;
    default:
      return undefined; // unknown sub-getter
  }
}

/** The system worst-case for a Δ display: `—` (absent), `∞` (structurally open),
 *  `≥lo`/`???` (data-gap open — a lower bound with unknown upper), else a
 *  duration. Uses the **system** dimension (the response-time SLA). */
function worstCaseLabel(e: LatencyEstimate | null): string {
  if (!e) return "—";
  const b = e.timed.system;
  if (b.open) return isStructural(b.reasons) ? "∞" : b.priced > 0 ? `≥${formatDuration(b.priced)}` : "???";
  return formatDuration(b.hi);
}

/**
 * Base→head **system** latency delta for the process diff (P4):
 * `system: 300ms → 800ms (+500ms)`. Absent-a-side renders `—`; a signed
 * numeric Δ shows only when both sides are finite. Returns null when neither
 * side has any system timing to show, so the caller can hide the chip.
 */
export function formatLatencyDelta(base: LatencyEstimate | null, head: LatencyEstimate | null): string | null {
  const closed = (e: LatencyEstimate | null): boolean => !!e && !e.timed.system.open;
  const emptyClosed = (e: LatencyEstimate | null): boolean => !e || (closed(e) && e.timed.system.hi === 0);
  if (emptyClosed(base) && emptyClosed(head)) return null; // nothing to show
  let delta = "";
  // A signed numeric Δ only when BOTH sides are CLOSED — comparing two
  // lower-bounds-with-unknown-upper would be a false "+200ms".
  if (closed(base) && closed(head)) {
    const d = head!.timed.system.hi - base!.timed.system.hi;
    delta = Math.abs(d) > 1e-9 ? ` (${d > 0 ? "+" : "−"}${formatDuration(Math.abs(d))})` : " (no change)";
  }
  return `system: ${worstCaseLabel(base)} → ${worstCaseLabel(head)}${delta}`;
}

/** Format a seconds value for display (`∞` for a structurally-open flow). Used
 *  by the end picker to label each end's estimate. */
export function formatLatency(seconds: number): string {
  return Number.isFinite(seconds) ? formatDuration(seconds) : "∞";
}

/** One dimension's interval label, or null when the dimension is empty (0). A
 *  `lo` of 0 with an open upper is "nothing priced yet" → just `???`. */
function dimLabel(b: Bound): string | null {
  if (!b.open && b.hi === 0 && b.lo === 0) return null;
  if (b.open) return b.lo > 0 ? `${formatDuration(b.lo)}–???` : "???";
  if (b.lo === 0) return `≤${formatDuration(b.hi)}`; // 0 lower → "you might skip it"
  return formatInterval(b.lo, b.hi);
}

/**
 * A one-line display string for the process badge / header. **System and
 * business are separate dimensions**, never a summed total: system (compute /
 * response) shows plain, business (human/wait) shows with a `wait` suffix —
 * `300ms · 1d wait`. A dimension that is 0 is omitted.
 */
export function formatEstimate(e: LatencyEstimate): string {
  const sys = dimLabel(e.timed.system);
  const bus = dimLabel(e.timed.business);
  const parts: string[] = [];
  if (sys) parts.push(sys);
  if (bus) parts.push(`${bus} wait`);
  if (parts.length === 0) return "0"; // pure freehand — the badge hook suppresses this
  const tail = !e.structurallyOpen && e.unestimated > 0 ? ` · ${e.unestimated} unestimated` : "";
  return `${parts.join(" · ")}${tail}`;
}

// =============================================================================
// Per-step timeline — the sequence-view ruler + per-hop labels (P3)
// =============================================================================

/** One step's own latency and the cumulative elapsed up to and including it,
 *  along the shown walk order. `open` = the upper is OPEN (unknown/unbounded). */
export interface StepLatency {
  readonly stepIndex: number;
  /** This step's own contribution (a call's callee latency, an `await` head). */
  readonly ownLo: number;
  readonly ownHi: number;
  readonly ownOpen: boolean;
  /** Running sum along walk order (a linear reading — a `parallel` reads as its
   *  branches summed, not maxed, so this can exceed the process badge total). */
  readonly cumLo: number;
  readonly cumHi: number;
  readonly cumOpen: boolean;
  /** Formatted own-latency label for the arrow (`200ms`, `50ms–500ms`, `???`);
   *  undefined when this step contributes nothing (a note, prose, `go`). */
  readonly label?: string;
  /** Formatted cumulative elapsed for the ruler (`340ms`, `1.2s–???`). */
  readonly cumLabel: string;
}

/**
 * Price every walk step (in shown order) for the sequence ruler + per-hop labels.
 * A **call** step's own time is its callee's `latency`; an **`await`** step's is
 * its (duration) head, else OPEN; notes/prose/`go` contribute nothing. Cumulative
 * is a linear running sum — the honest "elapsed reading each row in order",
 * distinct from the control-flow-aware process total (`estimateProcess`).
 */
export function perStepLatency(walk: WalkResult, env: LatencyEnv): StepLatency[] {
  const out: StepLatency[] = [];
  let cumLo = 0;
  let cumHi = 0;
  let cumOpen = false;
  for (const step of walk.steps) {
    const own = stepOwn(step, env);
    cumLo += own.lo;
    cumHi += own.hi === Infinity ? 0 : own.hi; // priced part of the running upper
    cumOpen = cumOpen || own.open;
    const cumLabel = cumOpen
      ? cumLo > 0
        ? `${formatDuration(cumLo)}–???`
        : "???" // nothing priced yet — no misleading 0
      : formatInterval(cumLo, cumHi);
    out.push({
      stepIndex: step.stepIndex,
      ownLo: own.lo,
      ownHi: own.hi,
      ownOpen: own.open,
      cumLo,
      cumHi: cumOpen ? Infinity : cumHi,
      cumOpen,
      cumLabel,
      ...(own.label ? { label: own.label } : {}),
    });
  }
  return out;
}

function stepOwn(step: WalkStep, env: LatencyEnv): { lo: number; hi: number; open: boolean; label?: string } {
  // await step: `await <head>` in noteText (nodeId `.../await:N`).
  if (isAwaitNodeId(step.nodeId) && step.noteText) {
    const head = step.noteText.replace(/^await\s+/, "").trim();
    const p = parseDuration(head);
    if (p.ok) return { lo: p.lo, hi: p.hi, open: false, label: formatInterval(p.lo, p.hi) };
    return { lo: 0, hi: Infinity, open: true, label: "???" };
  }
  // annotated note with a latency field — price as business time on the ruler.
  if (step.noteText !== undefined && !step.prose && !step.reentryTarget && step.noteLatency) {
    const p = parseDuration(step.noteLatency);
    if (p.ok) return { lo: p.lo, hi: p.hi, open: false, label: formatInterval(p.lo, p.hi) };
    return { lo: 0, hi: Infinity, open: true, label: "???" };
  }
  // note / prose / go / reentry — no time, no label.
  if (step.noteText !== undefined || step.prose || step.reentryTarget) {
    return { lo: 0, hi: 0, open: false };
  }
  // a call hop: price the callee interface.
  const look = env.latencyOf(step.to);
  if (look.kind === "value") {
    return { lo: look.lo, hi: look.hi, open: false, label: formatInterval(look.lo, look.hi) };
  }
  return { lo: 0, hi: Infinity, open: true, label: "???" };
}

/** A duration pinned onto one leaf by a test's `when` block (P4). `takes` pins
 *  the SYSTEM axis, `after` the BUSINESS axis; it OVERRIDES the leaf's declared
 *  `latency`/await-head (design §9 precedence: injected → declared → OPEN). */
export interface Injected {
  readonly axis: "system" | "business";
  readonly lo: number;
  readonly hi: number;
}

interface FoldCtx {
  readonly env: LatencyEnv;
  /** Resolved subprocess/process DECLS currently on the active `do` chain
   *  (recursion guard, keyed on decl identity — NOT the bare name, so two
   *  distinct same-named subprocesses in different modules don't collide). */
  readonly active: ReadonlySet<object>;
  /** Mutable sinks. `unest` is tracked per-bound (absorbable); these are the
   *  always-surfaced signals. */
  readonly sink: { malformed: string[]; contradiction: boolean };
  /** `\` snake runs PRESENT in the flow being folded (§4.13.4), by `runId`. A
   *  run is priced once, at its head, as the chain it means; a continuation
   *  whose head is in this flow contributes nothing on its own. Built per fold,
   *  so a pruned trace flow that executed a continuation without its head (a
   *  `go` re-entry) still prices that step standalone instead of nesting it
   *  under a promise that was never paid.
   *
   *  The head prices its tail from the continuation NODES rather than their
   *  callee paths, because a path cannot carry a per-node test pin — pricing
   *  from paths silently drops a `takes`/`after` on a continuation and passes a
   *  budget it should bust. */
  readonly runs: ReadonlyMap<string, RunMembers>;
  /** Per-leaf duration overrides from test pins, keyed by IR-node identity (P4).
   *  Absent ⇒ today's declared-latency fold, byte-for-byte. */
  readonly inject?: ReadonlyMap<ProcessNode, Injected>;
}

export interface EstimateOptions {
  /** Estimate as a spliced fragment (a subprocess): fold the body directly with
   *  no leading start-trigger split, since its leading `await`/inbound are real
   *  mid-flow waits at every splice site (matches `do`-splice demotion). */
  readonly fragment?: boolean;
  /** Test-pin duration overrides keyed by IR-node identity (P4). */
  readonly inject?: ReadonlyMap<ProcessNode, Injected>;
}

/** Apply a leaf's `inject` override (P4): the pinned duration REPLACES that
 *  axis's computed bound, then the two axes recombine. No entry ⇒ the passed
 *  bounds recombine unchanged (identical to a bare `recombine`). */
function applyInject(node: ProcessNode, ctx: FoldCtx, system: Bound, business: Bound): Timed {
  const inj = ctx.inject?.get(node);
  if (inj) {
    const b = closed(inj.lo, inj.hi);
    if (inj.axis === "system") system = b;
    else business = b;
  }
  return recombine(system, business);
}

/** One `\` run as it appears in the flow being folded. */
interface RunMembers {
  /** Present only if the run's head is in THIS flow. */
  head?: CallNode;
  /** The continuation nodes in this flow, in order. */
  readonly members: CallNode[];
}

/** `\` runs reachable in a flow — the runs priced as a whole chain at their
 *  head (§4.13.4). Derived from the flow ACTUALLY being folded, so a pruned
 *  trace that ran a continuation without its head prices it standalone, and a
 *  head whose continuations were pruned prices only itself. */
function collectRuns(body: Flow | undefined): ReadonlyMap<string, RunMembers> {
  const out = new Map<string, RunMembers>();
  const at = (id: string): RunMembers => {
    const cur = out.get(id);
    if (cur) return cur;
    const made: RunMembers = { members: [] };
    out.set(id, made);
    return made;
  };
  forEachNode(body, (n) => {
    if (n.kind !== "call") return;
    const c = n as CallNode;
    if (!c.runId) return;
    const entry = at(c.runId);
    if (c.runHead) entry.head = c;
    else entry.members.push(c);
  });
  return out;
}

/** Estimate one process (the default = longest-end / all-arms-combined fold). */
export function estimateProcess(model: ProcessModel, env: LatencyEnv, opts?: EstimateOptions): LatencyEstimate {
  const sink = { malformed: [] as string[], contradiction: false };
  const ctx: FoldCtx = {
    env,
    active: new Set(),
    sink,
    inject: opts?.inject,
    runs: collectRuns(model.body),
  };
  const timed = opts?.fragment ? foldFlow(model.body, ctx) : foldProcessBody(model.body, ctx);
  return {
    timed,
    structurallyOpen: timed.total.open && isStructural(timed.total.reasons),
    unestimated: timed.total.unest,
    malformed: sink.malformed,
    nestingContradiction: sink.contradiction,
  };
}

export interface ProcessEnd {
  /** `fail` / `finish` terminator, or the implicit `complete` fall-through. */
  readonly kind: "fail" | "finish" | "complete";
  /** Display label — a terminator's reason, or "complete". */
  readonly label: string;
  /** Worst-case cumulative to reach this end (seconds; ∞ if structurally open). */
  readonly estimate: number;
  /** Best-case cumulative (lower bound). */
  readonly min: number;
  /** True for the longest end (greatest worst-case) — the badge default. */
  readonly longest: boolean;
}

/**
 * Enumerate a process's **ends** — each `fail` / `finish` terminator plus the
 * fall-through "complete" — with the worst-case cumulative to reach each (the
 * end-picker data, decision 10). A path-DFS: terminators end a path; a branch /
 * select forks (each arm's terminators emit ends from the shared prefix) then
 * contributes its interval to the continuation. Parallel / loop internals are
 * priced as a unit (their nested terminators are not separate top-level ends in
 * v1). The greatest worst-case is flagged `longest` — it matches the badge.
 */
export function processEnds(model: ProcessModel, env: LatencyEnv): ProcessEnd[] {
  const sink = { malformed: [] as string[], contradiction: false };
  // The end-picker walks the same flow node-by-node, so it needs the same run
  // set — otherwise the ends would sum a snake run the badge nests.
  const ctx: FoldCtx = { env, active: new Set(), sink, runs: collectRuns(model.body) };
  // Strip the SAME leading start-trigger run the badge fold strips, then apply
  // the entry-promise envelope to EACH end — otherwise an inbound-triggered
  // process shows every end as ∞ while the badge shows a flat finite number.
  const lead = splitLeadingTrigger(model.body, ctx);
  const acc: Array<{ kind: ProcessEnd["kind"]; label: string; timed: Timed }> = [];
  const tail = collectEnds(lead.rest, ctx, ZERO, acc);
  if (tail.through) acc.push({ kind: "complete", label: "complete", timed: tail.cum });
  const base = acc.map((e) => {
    const t = applyEntryEnvelope(e.timed, lead, ctx);
    return { kind: e.kind, label: e.label, estimate: boundValue(t.total), min: t.total.lo };
  });
  let maxI = -1;
  let maxV = -Infinity;
  base.forEach((e, i) => {
    // Greatest worst-case; ties broken by the greater lower bound (design doc).
    if (e.estimate > maxV || (e.estimate === maxV && e.min > (base[maxI]?.min ?? -Infinity))) {
      maxV = e.estimate;
      maxI = i;
    }
  });
  return base.map((e, i) => ({ ...e, longest: i === maxI }));
}

function collectEnds(
  flow: Flow,
  ctx: FoldCtx,
  prefix: Timed,
  acc: Array<{ kind: ProcessEnd["kind"]; label: string; timed: Timed }>,
): { through: boolean; cum: Timed } {
  let cum = prefix;
  for (const node of flow) {
    if (node.kind === "terminator") {
      acc.push({ kind: node.outcome, label: node.reason?.text ?? node.outcome, timed: cum });
      return { through: false, cum };
    }
    if (node.kind === "owner_block") {
      // `Owner { … }` / `| { … }` / `dist { … }` is inlined flow — recurse so
      // its terminators are enumerated as ends; propagate its fall-through.
      const sub = collectEnds(node.body, ctx, cum, acc);
      if (!sub.through) return { through: false, cum: sub.cum };
      cum = sub.cum;
      continue;
    }
    if (node.kind === "splice") {
      // A spliced subprocess is inline flow — enumerate its terminators as ends
      // and let a `finish`/`fail` inside it end the host path (recursion-guarded).
      const r = resolveSplice(node, ctx);
      if (r && !ctx.active.has(r.decl)) {
        const inner: FoldCtx = { ...ctx, active: new Set([...ctx.active, r.decl]) };
        const sub = collectEnds(r.body, inner, cum, acc);
        if (!sub.through) return { through: false, cum: sub.cum };
        cum = sub.cum;
        continue;
      }
      cum = sumTimed([cum, foldNode(node, ctx)]); // unresolved / recursive → price as a unit
      continue;
    }
    if (node.kind === "branch") {
      // Emit each arm's terminators from the shared prefix; the continuation
      // folds ONLY the arms that fall through (a terminating arm never reaches
      // past the branch, so it must not inflate the downstream cumulative).
      const throughArms = node.arms.filter((arm) => (arm.body ? collectEnds(arm.body, ctx, cum, acc).through : true));
      const elseThrough = node.elseArm
        ? node.elseArm.body ? collectEnds(node.elseArm.body, ctx, cum, acc).through : true
        : undefined;
      const falls = throughArms.length > 0 || (node.elseArm ? !!elseThrough : true);
      if (!falls) return { through: false, cum }; // explicit else + every arm terminates
      const cont = { ...node, arms: throughArms, elseArm: elseThrough ? node.elseArm : undefined };
      cum = sumTimed([cum, foldNode(cont, ctx)]);
      continue;
    }
    if (node.kind === "select") {
      const throughCases = node.cases.filter((c) => (c.body ? collectEnds(c.body, ctx, cum, acc).through : true));
      cum = sumTimed([cum, foldNode({ ...node, cases: throughCases }, ctx)]);
      continue;
    }
    cum = sumTimed([cum, foldNode(node, ctx)]);
  }
  return { through: true, cum };
}

/** The leading start-trigger split of a process body: the run of leading
 *  awaits/inbound calls is the start trigger (excluded), the rest nests under
 *  the entry promise. `plo`/`phi` = the promise hull, `entryMissing` = leading
 *  inbound calls whose latency is missing/malformed. */
interface LeadSplit {
  readonly rest: Flow;
  readonly plo: number | null;
  readonly phi: number | null;
  readonly entryMissing: number;
}
function splitLeadingTrigger(body: Flow, ctx: FoldCtx): LeadSplit {
  let lead = 0;
  const entryPromises: Array<{ lo: number; hi: number }> = [];
  let entryMissing = 0;
  while (lead < body.length && isStartTrigger(body[lead]!)) {
    const n = body[lead]!;
    if (n.kind === "call") {
      const hop = n.hops[0];
      const look = hop ? ctx.env.latencyOf(hop.callee.interfacePath) : undefined;
      if (look?.kind === "value") entryPromises.push({ lo: look.lo, hi: look.hi });
      else if (hop) {
        entryMissing++;
        if (look?.kind === "malformed") ctx.sink.malformed.push(hop.callee.interfacePath.join("."));
      }
    }
    lead++;
  }
  return {
    rest: body.slice(lead),
    plo: entryPromises.length ? Math.min(...entryPromises.map((p) => p.lo)) : null,
    phi: entryPromises.length ? Math.max(...entryPromises.map((p) => p.hi)) : null,
    entryMissing,
  };
}

/** Wrap a folded body (or a single end's cumulative) in the entry envelope:
 *  nest its system under the promise hull, then OR a missing-entry gap. */
function applyEntryEnvelope(t: Timed, lead: LeadSplit, ctx: FoldCtx): Timed {
  let result = t;
  if (lead.plo !== null && lead.phi !== null) {
    const nested = nestUnderPromise(result, lead.plo, lead.phi);
    if (nested.contradiction) ctx.sink.contradiction = true;
    result = nested.timed;
  }
  if (lead.entryMissing > 0) result = withSystemGap(result, lead.entryMissing);
  return result;
}

/**
 * Fold a top-level process body: split off the leading start-trigger run
 * (awaits + inbound calls), nest the rest under the entry promise (if any).
 */
function foldProcessBody(body: Flow, ctx: FoldCtx): Timed {
  const lead = splitLeadingTrigger(body, ctx);
  return applyEntryEnvelope(foldFlow(lead.rest, ctx), lead, ctx);
}

/** OR a missing-latency gap onto a Timed's system component (opens it if closed). */
function withSystemGap(t: Timed, count: number): Timed {
  const s = t.system;
  const sys = open(s.lo, s.priced, [...s.reasons, "missing-latency"], s.unest + count);
  return recombine(sys, t.business);
}

function isStartTrigger(n: ProcessNode): boolean {
  if (n.kind === "await") return true;
  // A run head that `\` lines continue is not a bare trigger — it opens a
  // chain, exactly as the one-line spelling would, and must fold as one.
  if (n.kind === "call")
    return (
      n.hops.length === 1 &&
      (n as CallNode).runHead === undefined &&
      n.hops[0]!.direction === "inbound"
    );
  return false;
}

/** Fold an ordered run of nodes (a flow), summing sequentially. A terminator
 *  ends the run (nothing after it on this path contributes). */
function foldFlow(flow: Flow, ctx: FoldCtx): Timed {
  const parts: Timed[] = [];
  for (const node of flow) {
    parts.push(foldNode(node, ctx));
    if (node.kind === "terminator") break; // path ends here
  }
  return parts.length ? sumTimed(parts) : ZERO;
}

function foldNode(node: ProcessNode, ctx: FoldCtx): Timed {
  switch (node.kind) {
    case "call":
      return foldCall(node, ctx);
    case "note":
      return foldNote(node, ctx);
    case "await":
      return foldAwait(node, ctx);
    case "prose":
    case "description":
    case "terminator":
      return ZERO;
    case "reentry":
      return timedSystem(open(0, 0, ["go"]));
    case "branch":
      return foldBranch(node, ctx);
    case "select":
      return foldSelect(node, ctx);
    case "loop":
      return foldLoop(node, ctx);
    case "retry":
      return foldRetry(node, ctx);
    case "guard":
      return foldGuard(node, ctx);
    case "parallel":
      return foldParallel(node, ctx);
    case "reversible":
      return foldReversible(node, ctx);
    case "splice":
      return foldSplice(node, ctx);
    case "owner_block":
      return foldFlow(node.body, ctx);
    default:
      return ZERO;
  }
}

/** A leaf latency bound for one interface, recording gap/malformed into the sink. */
function latencyBound(path: QualifiedName, ctx: FoldCtx): Bound {
  const look = ctx.env.latencyOf(path);
  if (look.kind === "value") return closed(look.lo, look.hi);
  if (look.kind === "malformed") {
    ctx.sink.malformed.push(path.join("."));
    return open(0, 0, ["malformed-latency"], 1);
  }
  return open(0, 0, ["missing-latency"], 1);
}

/** What one `\` continuation contributes to its run head's tail: its declared
 *  hop latencies, or the pin that overrides them. A `takes`/`after` pin is
 *  keyed by NODE, so it can only be seen here — pricing the continuation by
 *  callee path instead would silently discard it. */
function hopBounds(node: CallNode, ctx: FoldCtx): Bound[] {
  const inj = ctx.inject?.get(node);
  if (inj && inj.axis === "system") return [closed(inj.lo, inj.hi)];
  return node.hops.map((h) => latencyBound(h.callee.interfacePath, ctx));
}

function foldCall(node: CallNode, ctx: FoldCtx): Timed {
  const hop0 = node.hops[0];
  if (!hop0) return ZERO;
  // A `\` continuation is already priced inside its run head's fold, as one
  // chain (§4.13.4). Only when the head is absent from THIS flow does the
  // continuation stand on its own.
  //
  // `runHead` is what separates a head from a continuation, and the head may
  // ITSELF be a `\`: a dangling snake opens a run at the draft floor, so
  // testing the glyph alone would make that head skip its own chain.
  const run = node.runId ? ctx.runs.get(node.runId) : undefined;
  if (node.owner.form === "snake" && node.runHead === undefined && run?.head) {
    return ZERO;
  }
  const inbound = hop0.direction === "inbound";
  const head = latencyBound(hop0.callee.interfacePath, ctx);

  // The run's tail: the rest of this line's hops, then every hop the `\` lines
  // continuing it contribute — exactly the tail of the chain it desugars to.
  // Each continuation is priced as a NODE so its own test pin is honoured; a
  // continuation that was pruned out of this flow contributes nothing.
  const tailBounds: Bound[] = node.hops.slice(1).map((h) => latencyBound(h.callee.interfacePath, ctx));
  if (node.runHead) {
    for (const m of run?.members ?? []) tailBounds.push(...hopBounds(m, ctx));
  }

  // System of the (possibly chained / snake-continued) call.
  let system: Bound;
  if (tailBounds.length === 0) {
    system = head;
  } else {
    const tail = sumB(tailBounds);
    if (!head.open) {
      // head promise caps the tail (nesting)
      const nested = nestUnderPromise(timedSystem(tail), head.lo, head.hi);
      if (nested.contradiction) ctx.sink.contradiction = true;
      system = nested.timed.system;
    } else {
      // undeclared head: tail stands in, head opens the system upper as a gap
      system = sumB([head, tail]);
    }
  }

  // Mid-flow inbound (`Them > Us.x`) = a wait for the incoming call (business),
  // plus the handler (system, above). Actor-caller business waits (decision 8 /
  // open question 3) are deferred — classifying them needs a caller≠owner /
  // participant test the IR direction already subsumes for the real cases.
  const busBound = inbound ? open(0, 0, ["inbound-wait"]) : ZERO_BOUND;
  return applyInject(node, ctx, system, busBound);
}

function foldNote(node: import("./process-model.js").NoteNode, ctx: FoldCtx): Timed {
  const raw = noteLatencyRaw(node);
  if (raw === undefined) return applyInject(node, ctx, ZERO_BOUND, ZERO_BOUND); // unannotated note = 0
  const parsed = parseDuration(raw);
  if (parsed.ok) return applyInject(node, ctx, ZERO_BOUND, closed(parsed.lo, parsed.hi));
  if (parsed.reason === "not-time" || parsed.reason === "malformed") {
    ctx.sink.malformed.push(`note:${raw}`);
  }
  return applyInject(node, ctx, ZERO_BOUND, open(0, 0, ["missing-latency"], 1));
}

/** A note's `latency` field body value, if authored (grammar addition). */
function noteLatencyRaw(node: import("./process-model.js").NoteNode): string | undefined {
  const fields = (node as { fields?: readonly import("./types.js").FieldDecl[] }).fields;
  if (!fields) return undefined;
  const f = fields.find((x) => x.path.length === 1 && x.path[0] === "latency");
  if (!f) return undefined;
  return valueText(f.value);
}

function valueText(v: import("./types.js").Value | undefined): string | undefined {
  if (!v) return undefined;
  if (v.kind === "identifier") return v.text;
  if (v.kind === "string") return v.text;
  return undefined;
}

function foldAwait(node: AwaitNode, ctx: FoldCtx): Timed {
  const head = node.target.text.trim();
  const parsed = parseDuration(head);
  if (parsed.ok) return applyInject(node, ctx, ZERO_BOUND, closed(parsed.lo, parsed.hi));
  if (isCronShape(head)) {
    return applyInject(node, ctx, ZERO_BOUND, open(0, 0, ["cron-wait"], 1));
  }
  // condition wait — structural
  return applyInject(node, ctx, ZERO_BOUND, open(0, 0, ["condition-wait"]));
}

/** Rough cron-shape test: 5 whitespace-separated fields of digits/`*`/`,`/`-`/`/`. */
function isCronShape(s: string): boolean {
  const parts = s.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  return parts.every((p) => /^[\d*,/-]+$/.test(p));
}

function foldBranch(node: import("./process-model.js").BranchNode, ctx: FoldCtx): Timed {
  const arms: Timed[] = node.arms.map((a) => (a.body ? foldFlow(a.body, ctx) : ZERO));
  if (node.elseArm) arms.push(node.elseArm.body ? foldFlow(node.elseArm.body, ctx) : ZERO);
  else arms.push(ZERO); // no else → a 0-cost fall-through arm
  return altTimed(arms);
}

function foldSelect(node: import("./process-model.js").SelectNode, ctx: FoldCtx): Timed {
  const cases: Timed[] = node.cases.map((c) => (c.body ? foldFlow(c.body, ctx) : ZERO));
  // lo = 0 (a case may not match); the count decides the upper.
  if (node.count.kind === "one") {
    const combined = maxTimed(cases); // max case
    return zeroLo(combined);
  }
  if (node.count.kind === "n") {
    // Σ of the n costliest cases (by total hi).
    const n = node.count.n;
    const costliest = topNByTotal(cases, n);
    return zeroLo(sumTimed(costliest));
  }
  // all: Σ every case
  return zeroLo(sumTimed(cases));
}

/** Set every bucket's lo to 0 (branch/select may take no case). */
function zeroLo(t: Timed): Timed {
  const z = (b: Bound): Bound => ({ ...b, lo: 0 });
  return { total: z(t.total), system: z(t.system), business: z(t.business) };
}

function totalHi(t: Timed): number {
  return t.total.open ? Infinity : t.total.hi;
}
function topNByTotal(ts: readonly Timed[], n: number): Timed[] {
  return [...ts].sort((a, b) => totalHi(b) - totalHi(a)).slice(0, Math.max(0, n));
}

function foldLoop(node: import("./process-model.js").LoopNode, ctx: FoldCtx): Timed {
  const body = node.body ? foldFlow(node.body, ctx) : ZERO;
  const n = numericHead(node.head);
  if (n !== null) return repeat(body, n);
  // collection or unbounded head → unknown count
  return openLoop(body);
}

/** A numeric loop head (`each 3`) → the count; else null. */
function numericHead(head: import("./process-model.js").LoopHead | undefined): number | null {
  if (!head?.label || head.collection) return null;
  const t = head.label.text.trim();
  return /^\d+$/.test(t) ? Number.parseInt(t, 10) : null;
}

function repeat(t: Timed, n: number): Timed {
  if (n <= 0) return ZERO;
  return sumTimed(Array.from({ length: n }, () => t));
}

/** Unknown iteration count: lo = 0 (may be empty), upper open (gap). The +1
 *  "uncounted loop" gap rides on `total`; buckets keep the body's own gap counts. */
function openLoop(body: Timed): Timed {
  return {
    total: open(0, body.total.priced, ["unbounded-each"], body.total.unest + 1),
    system: open(0, body.system.priced, ["unbounded-each"], body.system.unest),
    business: open(0, body.business.priced, ["unbounded-each"], body.business.unest),
  };
}

function foldRetry(node: import("./process-model.js").RetryNode, ctx: FoldCtx): Timed {
  const attempt = foldFlow(node.attempt, ctx);
  const catchT = maxTimed(node.catches.map((c) => (c.body ? foldFlow(c.body, ctx) : ZERO)));
  const elseT = node.elseBody ? foldFlow(node.elseBody, ctx) : ZERO;
  const n = numericHead(node.bound);
  if (n === null || n <= 0) {
    return openLoop(attempt); // unknown bound
  }
  // worst = n·attempt + (n−1)·catch + else ; best = attempt.lo
  const worstParts: Timed[] = [
    ...Array.from({ length: n }, () => attempt),
    ...Array.from({ length: Math.max(0, n - 1) }, () => catchT),
    elseT,
  ];
  const worst = sumTimed(worstParts);
  // best case: a single successful attempt.
  return withLo(worst, attempt.total.lo, attempt.system.lo, attempt.business.lo);
}

function withLo(t: Timed, totalLo: number, sysLo: number, busLo: number): Timed {
  return {
    total: { ...t.total, lo: totalLo },
    system: { ...t.system, lo: sysLo },
    business: { ...t.business, lo: busLo },
  };
}

function foldGuard(node: import("./process-model.js").GuardNode, ctx: FoldCtx): Timed {
  const attempt = foldFlow(node.attempt, ctx);
  const catchT = maxTimed(node.catches.map((c) => (c.body ? foldFlow(c.body, ctx) : ZERO)));
  // [attempt.lo, attempt.hi + catch.hi]
  const sum = sumTimed([attempt, catchT]);
  return withLo(sum, attempt.total.lo, attempt.system.lo, attempt.business.lo);
}

function foldParallel(node: import("./process-model.js").ParallelNode, ctx: FoldCtx): Timed {
  // Boundary timeout `parallel await d { W } else { E }` → race(W, await d ; E).
  // The IR keeps the work as the single implicit body branch.
  if (node.awaitClause) {
    const w = workBranches(node.body, ctx);
    const timerBranch = sumTimed([
      foldAwaitHead(node.awaitClause.head.text, ctx),
      node.awaitClause.elseBody ? foldFlow(node.awaitClause.elseBody, ctx) : ZERO,
    ]);
    return minTimed([...w, timerBranch]);
  }

  if (node.merge.detached) return ZERO; // `out` — excluded from completion time

  const body = node.body;
  // `parallel select` — concurrent inclusive cases; which run is a runtime
  // decision, so lo = 0 and the upper is the costliest case (max, concurrent).
  if (body.form === "select") {
    const cases = body.branches.map((b) => foldFlow(b.body, ctx));
    return zeroLo(maxTimed(cases));
  }
  // `parallel each` fan-out — identical bodies run concurrently, so the width
  // never extends the max: a numeric bound keeps the body interval; a collection
  // may be empty, so lo drops to 0.
  if (body.form === "fanOut") {
    const one = body.body ? foldFlow(body.body, ctx) : ZERO;
    return numericHead(body.head) !== null ? one : zeroLo(one);
  }

  // form === "branches"
  const named = new Map<string, Timed>();
  const branches: Timed[] = body.branches.map((b) => {
    const t = foldFlow(b.body, ctx);
    if (b.name) named.set(b.name.name, t);
    return t;
  });
  const threshold = node.merge.threshold;
  if (threshold?.kind === "boolean") {
    // `join all` parses as a boolean threshold named "all" (parser has no `all`
    // keyword) — treat it as join-all (max over EVERY branch), not a lookup of a
    // branch named "all" (which would vacuously resolve to 0).
    if (threshold.expr.kind === "name" && threshold.expr.name === "all" && !named.has("all")) {
      return maxTimed(branches);
    }
    // min over satisfying sets of (max within set): `and` = max, `or` = min.
    return evalBoolMerge(threshold.expr, named);
  }
  const policy = node.merge.policy;
  if (policy === "race") {
    const n = threshold?.kind === "count" ? threshold.n : 1;
    return orderStatTimed(branches, n);
  }
  if (threshold?.kind === "count") {
    return orderStatTimed(branches, threshold.n);
  }
  return maxTimed(branches); // join all / AND-join
}

/** The work branch(es) of a parallel body, ignoring merge policy (used by the
 *  `parallel await … else` desugar, whose W is the implicit body branch). */
function workBranches(body: import("./process-model.js").ParallelBody, ctx: FoldCtx): Timed[] {
  if (body.form === "fanOut") return [body.body ? foldFlow(body.body, ctx) : ZERO];
  return body.branches.map((b) => foldFlow(b.body, ctx));
}

/** Evaluate a boolean merge threshold over the named branches: `and` = the
 *  slowest of the set (max), `or` = the cheapest satisfying set (min). A name
 *  with no matching branch (a typo / unknown branch) is treated as an unknown
 *  wait — structural-open — NOT vacuous 0: a 0 would silently collapse an `or`
 *  min and let an over-budget parallel slip past a latency policy. */
function evalBoolMerge(
  expr: import("./process-model.js").BoolExpr,
  named: ReadonlyMap<string, Timed>
): Timed {
  if (expr.kind === "name") return named.get(expr.name) ?? timedSystem(open(0, 0, ["condition-wait"]));
  const terms = expr.terms.map((t) => evalBoolMerge(t, named));
  return expr.kind === "and" ? maxTimed(terms) : minTimed(terms);
}

function foldAwaitHead(head: string, ctx: FoldCtx): Timed {
  const parsed = parseDuration(head.trim());
  if (parsed.ok) return timedBusiness(closed(parsed.lo, parsed.hi));
  if (isCronShape(head)) {
    return timedBusiness(open(0, 0, ["cron-wait"], 1));
  }
  return timedBusiness(open(0, 0, ["condition-wait"]));
}

function foldReversible(node: import("./process-model.js").ReversibleNode, ctx: FoldCtx): Timed {
  // Forward items in sequence; unwind (compensation) excluded in v1.
  const parts: Timed[] = [];
  for (const item of node.items) {
    if (!item.forward) continue;
    if (item.forward.kind === "block") parts.push(foldFlow(item.forward.body, ctx));
    else parts.push(foldNode(item.forward, ctx));
  }
  return parts.length ? sumTimed(parts) : ZERO;
}

/** Resolve a `do` target to its decl (subprocess first, then process) + body.
 *  Returns the decl OBJECT as the recursion key (identity, not name — a mixed
 *  qualified/bare spelling resolves to the same object, distinct same-named
 *  decls to different ones). */
function resolveSplice(
  node: import("./process-model.js").SpliceNode,
  ctx: FoldCtx,
): { body: Flow; decl: object } | null {
  const name = node.target.join(".");
  const bareName = node.target[node.target.length - 1]!;
  // A QUALIFIED target resolves only by its full spelling — never a bare
  // fallback to an unrelated same-named decl in another module (which would
  // fabricate a cycle: `Root.Mid.Shared` doing `Root.Other.Shared` must not
  // land back on `Shared`). Bare fallback is for genuinely bare targets only.
  const bare = node.target.length === 1;
  const sub = ctx.env.subprocessByName.get(name) ?? (bare ? ctx.env.subprocessByName.get(bareName) : undefined);
  const proc = sub ? undefined : ctx.env.processByName?.get(name);
  const decl = sub ?? proc;
  if (!decl?.body) return null; // forward-referenced / out-of-scope — draws, no time
  return { body: decl.body, decl };
}

function foldSplice(node: import("./process-model.js").SpliceNode, ctx: FoldCtx): Timed {
  const r = resolveSplice(node, ctx);
  if (!r) return ZERO;
  if (ctx.active.has(r.decl)) return timedSystem(open(0, 0, ["recursive-splice"]));
  const inner: FoldCtx = {
    ...ctx,
    active: new Set([...ctx.active, r.decl]),
    // `runId` is body-local, so the spliced body brings its OWN runs.
    // Inheriting the caller's map gets it wrong twice over: this body's heads
    // are absent (its continuations would be priced standalone, on top of the
    // head's own tail) and a same-numbered id from the caller could claim one.
    runs: collectRuns(r.body),
  };
  // Spliced: leading triggers demote to mid-flow (no start-trigger exclusion).
  return foldFlow(r.body, inner);
}
